/**
 * sophior-keuzehulp.js
 * Interactieve productkeuzehulp (snijplanken). Progressive enhancement bovenop
 * een crawlbaar keuzedocument: stap-navigatie, transparante gewogen scoring op de
 * server-gerenderde JSON-index, resultaat met onderbouwing, deelbare resultaat-URL,
 * toegankelijkheid en GA4-hooks. Vanilla JS, geen web components.
 */
(function () {
  /* ---------- helpers ---------- */
  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }
  // Registercorrecties (SCHAKELAARS §4.1 _tekstcorrecties): de poort zet de body-klasse; het script
  // laadt defer, dus body bestaat al. Alleen de redenen van het snijplank-profiel lezen dit.
  var TEKSTCORRECTIES = !!(document.body && document.body.classList.contains('sophior-uitrol--tekstcorrecties'));

  // Gedeelde helper: wordt alleen door het snijplank-profiel gebruikt.
  function woodName(p) {
    return (p.houtsoort || '').replace(/hout$/i, '') || 'hout';
  }

  /* ---------- profielregister ----------
   * Eén engine, meerdere keuzehulpen. Elk profiel beschrijft zijn eigen facetten,
   * cadeau-facet, lead-sleutel, per-facet scoring, onderbouwing en tiebreak.
   * Het actieve profiel wordt in initSection gekozen op root.dataset.keuzehulpType. */
  var PROFILES = {
    snijplank: {
      facets: ['gebruik', 'messen', 'uitstraling', 'ruimte', 'onderhoud'],
      giftFacet: 'gebruik',
      // facetMatch: 0..1 hoe goed 'product' past bij het gekozen antwoord voor 'facet'.
      facetMatch: function (p, facet, value) {
        switch (facet) {
          case 'gebruik':
            if (value === 'dagelijks') return p.geschikt.dagelijks ? 1 : 0.4;
            if (value === 'zwaar') {
              if (p.geschikt.zwaar) return 1;
              return (p.size_bucket === 'xl' || p.size_bucket === 'groot') ? 0.6 : 0.2;
            }
            if (value === 'serveren') return p.geschikt.serveren ? 1 : 0.3;
            if (value === 'cadeau') return p.bestseller ? 1 : 0.2;
            return 0.5;
          case 'messen':
            if (value === 'japans') {
              var base = p.mesv >= 3 ? 1 : (p.mesv === 2 ? 0.6 : 0.3);
              if (p.constructie === 'kopshout') base = Math.min(1, base + 0.1);
              return base;
            }
            if (value === 'goede') return p.mesv >= 3 ? 1 : (p.mesv === 2 ? 0.85 : 0.6);
            if (value === 'basis') return p.constructie === 'langshout' ? 0.75 : 0.7;
            if (value === 'minder') return p.constructie === 'langshout' ? 0.6 : 0.4;
            return 0.5;
          case 'uitstraling':
            // Drie tintsmaken (KENNISFEITEN r.28-32): donker (walnoot, acacia kopshout),
            // warm (kers, acacia langshout), licht (esdoorn).
            if (value === 'donker') return ({ donker: 1, warm: 0.35, licht: 0.2 })[p.tint] || 0.3;
            if (value === 'warm') return ({ warm: 1, donker: 0.4, licht: 0.4 })[p.tint] || 0.3;
            if (value === 'licht') return ({ licht: 1, warm: 0.45, donker: 0.2 })[p.tint] || 0.3;
            return 0.65; // neutraal
          case 'ruimte':
            if (value === 'weinig') return ({ medium: 1, groot: 0.4, set: 0.4, xl: 0.1 })[p.size_bucket] || 0.5;
            if (value === 'gemiddeld') return ({ medium: 0.8, groot: 1, set: 0.7, xl: 0.4 })[p.size_bucket] || 0.6;
            if (value === 'veel') return ({ medium: 0.3, groot: 0.8, set: 0.7, xl: 1 })[p.size_bucket] || 0.5;
            // Set-route: aparte plank voor rauw vlees of serveren.
            if (value === 'meerdere') return ({ set: 1, groot: 0.5, medium: 0.45, xl: 0.3 })[p.size_bucket] || 0.5;
            return 0.5;
          case 'onderhoud':
            // Koopmotief-vraag: premium (walnoot/kers), prijs (acacia), krasvast (esdoorn/acacia).
            if (value === 'premium') {
              var pm = p.mesv >= 3 ? 0.95 : (p.mesv === 2 ? 0.55 : 0.35);
              if (p.constructie === 'kopshout') pm = Math.min(1, pm + 0.05);
              return pm;
            }
            if (value === 'prijs') {
              if (p.houtsoort === 'Acaciahout') return p.constructie === 'langshout' ? 1 : 0.9;
              if (p.houtsoort === 'Kersenhout' || p.houtsoort === 'Esdoornhout') return 0.7;
              if (p.houtsoort === 'Walnoothout') return 0.3;
              return 0.4;
            }
            if (value === 'krasvast') {
              if (p.houtsoort === 'Esdoornhout') return 1;
              if (p.houtsoort === 'Acaciahout') return 0.95;
              if (p.houtsoort === 'Walnoothout') return 0.45;
              if (p.houtsoort === 'Kersenhout') return 0.4;
              return 0.4;
            }
            if (value === 'geen') return 0.6;
            // Oude waarden (gedeelde URLs).
            if (value === 'minimaal') return p.constructie === 'langshout' ? 1 : 0.4;
            if (value === 'graag') return p.constructie === 'kopshout' ? 1 : 0.6;
            return 0.7;
          default:
            return 0.5;
        }
      },
      buildReasons: function (p, answers) {
        var reasons = [];
        var wood = woodName(p);
        var m = answers.messen;
        if ((m === 'japans' || m === 'goede') && p.mesv >= 2) {
          var messenTxt = m === 'japans' ? 'is ideaal voor fijne Japanse messen' : 'spaart je (koks)messen';
          if (p.constructie === 'kopshout') {
            reasons.push('Mesvriendelijk ' + wood.toLowerCase() + ' in kopshout: het gedeeltelijk zelfsluitende oppervlak ' + messenTxt + '.');
          } else {
            reasons.push('Mesvriendelijk ' + wood.toLowerCase() + ', ' + messenTxt.replace(/^is /, '') + '.');
          }
        } else if (m === 'minder' || (m && p.mesv === 1)) {
          reasons.push('Hard en betaalbaar, en nog altijd mesvriendelijker dan plastic, glas of titanium.');
        }

        var g = answers.gebruik;
        if (g === 'zwaar' && (p.constructie === 'kopshout' || p.size_bucket === 'xl' || p.size_bucket === 'groot')) {
          reasons.push('Dik en stevig, gemaakt voor zwaar hak- en snijwerk.');
        } else if (g === 'serveren' && (p.tint === 'donker' || p.geschikt.serveren)) {
          reasons.push('Mooi genoeg om zo op tafel te zetten als serveerplank.');
        } else if (g === 'dagelijks') {
          reasons.push('Fijne maat en constructie voor dagelijks snijwerk.');
        } else if (g === 'cadeau' && p.bestseller) {
          reasons.push('Een van onze bestsellers, een veilige keuze als cadeau.');
        } else if (g === 'cadeau') {
          reasons.push('Een cadeau met blijvende waarde: massief hout dat jaren meegaat.');
        }

        var o = answers.onderhoud;
        // Registercorrectie (verbeterronde 27 okt 2026, KENNISFEITEN §2 houtzinnen; PLAN §4b): achter
        // de schakelaar 'Teksten: registercorrecties' (body-klasse sophior-uitrol--tekstcorrecties)
        // "het beste voor je messen" alleen bij walnoot (kers en esdoorn zijn ook zeer mesvriendelijk,
        // maar walnoot is binnen het assortiment het meest vergevingsgezind) en geen "krasvast" meer.
        // Zonder de klasse de oude zinnen, ongewijzigd.
        if (TEKSTCORRECTIES && o === 'premium' && p.mesv >= 3) {
          if (p.houtsoort === 'Walnoothout') {
            reasons.push('Het beste voor je messen, met de warme, luxe uitstraling van walnoot.');
          } else if (p.houtsoort === 'Kersenhout') {
            reasons.push('Zeer mesvriendelijk, met een warme roodbruine tint die door licht donkerder wordt.');
          } else if (p.houtsoort === 'Esdoornhout') {
            reasons.push('Zeer mesvriendelijk, ondanks de hardheid: de structuur is fijn en glad.');
          } else {
            reasons.push('Zeer mesvriendelijk hout.');
          }
        } else if (TEKSTCORRECTIES && o === 'prijs' && p.houtsoort === 'Acaciahout') {
          reasons.push('Massief, hard acaciahout voor een scherpe prijs: veel plank voor je geld.');
        } else if (TEKSTCORRECTIES && o === 'krasvast' && p.houtsoort === 'Esdoornhout') {
          reasons.push('Esdoorn is met 1.450 lbf het hardste hout van ons assortiment.');
        } else if (TEKSTCORRECTIES && o === 'krasvast' && p.houtsoort === 'Acaciahout') {
          reasons.push('Acacia is hard: 1.430 lbf, na esdoorn het hardste hout van ons assortiment.');
        } else if (o === 'premium' && p.mesv >= 3) {
          reasons.push('Onze premiumkeuze: het mooiste hout uit het assortiment, en het meest vergevingsgezind voor je messen.');
        } else if (o === 'prijs' && p.houtsoort === 'Acaciahout') {
          reasons.push('Massief acaciahout voor een scherpe prijs: veel plank voor je geld.');
        } else if (o === 'krasvast' && p.houtsoort === 'Esdoornhout') {
          reasons.push('Esdoorn is met een Janka-hardheid van 1.450 lbf het hardste hout uit ons assortiment: krasvast en vormvast.');
        } else if (o === 'krasvast' && p.houtsoort === 'Acaciahout') {
          reasons.push('Acacia is hard en krasvast (Janka 1.430 lbf): kan jarenlang tegen een stootje.');
        } else if (o === 'minimaal' && p.constructie === 'langshout') {
          reasons.push('Licht en dun, dus weinig gedoe in het dagelijks gebruik.');
        }

        var u = answers.uitstraling;
        if (u === 'donker' && p.tint === 'donker') reasons.push('Warme, donkere uitstraling.');
        else if (u === 'warm' && p.tint === 'warm') reasons.push('Warme, karaktervolle tint die door de jaren heen mooier wordt.');
        else if (u === 'licht' && p.tint === 'licht') reasons.push('Licht crème, en dat blijft zo: de kleur verandert nauwelijks.');

        var r = answers.ruimte;
        if (r === 'weinig' && p.size_bucket === 'medium') reasons.push('Compact formaat, ideaal voor een kleine keuken.');
        else if (r === 'veel' && (p.size_bucket === 'xl' || p.size_bucket === 'groot')) reasons.push('Royaal werkblad om ruim te preppen.');
        else if (r === 'meerdere' && p.size_bucket === 'set') reasons.push('Twee planken: altijd een aparte plank voor rauw vlees, of om mee te serveren.');

        return reasons.slice(0, 3);
      },
      // Gelijkspel: mesvriendelijkste eerst, daarna kopshout (premium) vóór langshout;
      // de engine sorteert daarna zelf op laagste prijs.
      tiebreak: function (a, b) {
        var d = (b.mesv || 0) - (a.mesv || 0);
        if (d !== 0) return d;
        if (a.constructie !== b.constructie) return a.constructie === 'kopshout' ? -1 : 1;
        return 0;
      }
    },

    pan: {
      // 'warmtebron' blijft in de lijst voor oude gedeelde URLs: zonder vraag/gewicht
      // skipt scoreProduct dat facet (w <= 0).
      facets: ['kookstijl', 'gerechten', 'personen', 'onderhoud', 'inhuis', 'warmtebron'],
      giftFacet: 'kookstijl',
      // facetMatch: 0..1 hoe goed 'product' past bij het gekozen antwoord voor 'facet'.
      facetMatch: function (p, facet, value) {
        switch (facet) {
          case 'kookstijl':
            if (value === 'controle') return p.pantype === 'rvs' ? 1 : 0.4;
            if (value === 'moeiteloos') return p.pantype === 'hybride' ? 1 : 0.35;
            // Bij twijfel wint hybride licht (laagdrempeliger, minder kans op teleurstelling).
            if (value === 'allebei') return p.pantype === 'hybride' ? 0.75 : 0.65;
            if (value === 'cadeau') return p.bestseller ? 1 : 0.3;
            return 0.5;
          case 'gerechten':
            // 'vlees' is de oude waarde (gedeelde URLs); scoort als 'bakken'.
            if (value === 'bakken' || value === 'vlees') {
              if (p.size_bucket === 'set') return 0.9;
              return ({ koekenpan: 1, wok: 0.3, hapjes: 0.6 })[p.vorm] || 0.6;
            }
            if (value === 'roerbak') {
              if (p.size_bucket === 'set') return 0.3;
              return ({ koekenpan: 0.35, wok: 1, hapjes: 0.6 })[p.vorm] || 0.5;
            }
            if (value === 'sauzen') {
              if (p.size_bucket === 'set') return 0.35;
              return ({ koekenpan: 0.4, wok: 0.55, hapjes: 1 })[p.vorm] || 0.5;
            }
            // Bewust zwak koekenpan-vangnet: een sterk inhuis-signaal kan het overrulen.
            if (value === 'alles') {
              if (p.size_bucket === 'set') return 0.7;
              return ({ koekenpan: 0.65, wok: 0.45, hapjes: 0.6 })[p.vorm] || 0.6;
            }
            // Oude waarde (gedeelde URLs).
            if (value === 'delicaat') {
              if (p.pantype === 'hybride') return 1;
              if (p.vorm === 'wok') return 0.3;
              return 0.45;
            }
            return 0.5;
          case 'personen':
            if (value === 'p12') return ({ p12: 1, p24: 0.45, p34: 0.25, set: 0.55 })[p.size_bucket] || 0.5;
            if (value === 'p24') return ({ p12: 0.35, p24: 1, p34: 0.6, set: 0.8 })[p.size_bucket] || 0.5;
            // 5+ personen: Ø28 (p24) scoort maximaal — maatadvies "bij twijfel de gezinsmaat".
            if (value === 'p34') return ({ p12: 0.15, p24: 1, p34: 0.85, set: 0.9 })[p.size_bucket] || 0.5;
            if (value === 'wisselend') return ({ p12: 0.4, p24: 0.6, p34: 0.5, set: 1 })[p.size_bucket] || 0.5;
            return 0.5;
          case 'onderhoud':
            // Koopmotief-vraag: PFAS/kookplaat zijn neutraal (geruststelling in buildReasons),
            // levensduur/schoonmaak geven materiaal een licht duwtje.
            if (value === 'pfasvrij' || value === 'warmtebron') return 0.7;
            if (value === 'levensduur') return p.pantype === 'rvs' ? 0.85 : 0.55;
            if (value === 'schoonmaak') return p.pantype === 'hybride' ? 0.85 : 0.55;
            // Oude waarden (gedeelde URLs).
            if (value === 'vaatwasser') return p.pantype === 'rvs' ? 1 : 0.55;
            if (value === 'handwas' || value === 'verzorgen') return 0.8;
            return 0.7;
          case 'inhuis':
            if (value === 'niets') {
              if (p.size_bucket === 'set') return 0.7;
              return ({ koekenpan: 0.8, wok: 0.55, hapjes: 0.6 })[p.vorm] || 0.6;
            }
            // Tweede route voor wok en hapjespan; welke van de twee wint bepaalt 'gerechten'.
            if (value === 'uitbreiden') {
              if (p.size_bucket === 'set') return 0.15;
              return ({ koekenpan: 0.2, wok: 1, hapjes: 1 })[p.vorm] || 0.5;
            }
            if (value === 'naar_rvs') return p.pantype === 'rvs' ? 1 : 0.3;
            if (value === 'naar_hybride') return p.pantype === 'hybride' ? 1 : 0.3;
            return 0.5;
          case 'warmtebron':
            return 0.5; // oude vraag; weegt niet mee (geen block meer in het template)
          default:
            return 0.5;
        }
      },
      // Alleen POSITIEVE redenen — merkregel: nooit de ene lijn afvallen om de andere te verkopen.
      buildReasons: function (p, answers) {
        var reasons = [];

        // 1) Geruststelling op het koopmotief: dit noemde de bezoeker zelf het belangrijkst.
        if (answers.onderhoud === 'pfasvrij') {
          reasons.push('100% PFAS-vrij: geen PFAS, PTFE of PFOA, niet in het product en niet in het productieproces.');
        } else if (answers.onderhoud === 'warmtebron' || answers.warmtebron === 'inductie') {
          reasons.push('Werkt op elke warmtebron, inclusief inductie, dankzij de magnetische RVS-buitenlaag.');
        } else if (answers.onderhoud === 'levensduur' && p.pantype === 'rvs') {
          reasons.push('Zonder coating kan er niets slijten: deze pan gaat tientallen jaren mee.');
        } else if (answers.onderhoud === 'levensduur' && p.pantype === 'hybride') {
          reasons.push('5 jaar garantie en 5 tot 10 jaar antiaanbakwerking bij goed gebruik.');
        } else if (answers.onderhoud === 'schoonmaak' && p.pantype === 'hybride') {
          reasons.push('Niets bakt aan, dus schoonmaken is zo gebeurd; handwas houdt de laag het langst mooi.');
        } else if ((answers.onderhoud === 'schoonmaak' || answers.onderhoud === 'vaatwasser') && p.pantype === 'rvs') {
          reasons.push('Vaatwasserbestendig; handwas houdt de glans langer mooi.');
        }

        // 2) Materiaal (kookstijl, of het inhuis-signaal als dat de doorslag gaf).
        if (answers.kookstijl === 'controle' && p.pantype === 'rvs') {
          reasons.push('Tri-ply RVS geeft je maximale controle en aanbraadkracht: even voorverwarmen, druppeltest en je krijgt de mooiste korst.');
        } else if (answers.kookstijl === 'moeiteloos' && p.pantype === 'hybride') {
          reasons.push('Moeiteloos dagelijks koken mét aanbraadkracht, dankzij de PFAS-vrije keramische laag in de RVS-honingraat.');
        } else if (answers.kookstijl === 'allebei' && p.pantype === 'hybride') {
          reasons.push('Een veelzijdige allrounder: moeiteloos in gebruik, met de aanbraadkracht van tri-ply RVS.');
        } else if (answers.kookstijl === 'cadeau' && p.bestseller) {
          reasons.push('Een van onze bestsellers, een veilige keuze als cadeau.');
        } else if (answers.inhuis === 'naar_rvs' && p.pantype === 'rvs') {
          reasons.push('Een blijvende upgrade: tri-ply RVS zonder coating die kan slijten.');
        } else if (answers.inhuis === 'naar_hybride' && p.pantype === 'hybride') {
          reasons.push('Het gemak dat je zoekt, zonder PFAS: de keramische laag ligt verzonken in de RVS-honingraat.');
        }

        // 3) Vorm (gerechten eerst, anders de uitbreidingsroute).
        if (answers.gerechten === 'roerbak' && p.vorm === 'wok') {
          reasons.push('Ø30 cm met hoge wanden: gemaakt voor roerbakken en grote porties.');
        } else if (answers.gerechten === 'sauzen' && p.vorm === 'hapjes') {
          reasons.push('Hoge rand en royale bodem: ideaal voor aanbraden, sauzen en eenpansgerechten.');
        } else if ((answers.gerechten === 'bakken' || answers.gerechten === 'vlees') && p.vorm === 'koekenpan') {
          reasons.push('Gemaakt voor bakken en braden: de 3,5 mm dikke bodem verdeelt de warmte gelijkmatig.');
        } else if (answers.inhuis === 'uitbreiden' && p.vorm === 'wok') {
          reasons.push('De perfecte aanvulling op je koekenpan: roerbakken doe je voortaan in de wok.');
        } else if (answers.inhuis === 'uitbreiden' && p.vorm === 'hapjes') {
          reasons.push('De logische tweede pan naast je koekenpan: voor sauzen, stoven en eenpansgerechten.');
        } else if (answers.inhuis === 'niets' && p.vorm === 'koekenpan' && p.size_bucket !== 'set') {
          reasons.push('De ideale eerste pan: hiermee kook je vrijwel alles.');
        } else if (answers.gerechten === 'delicaat' && p.pantype === 'hybride') {
          reasons.push('Eieren, vis en pannenkoeken lukken moeiteloos, zonder PFAS.');
        }

        // 4) Maat.
        if (answers.personen === 'p12' && p.size_bucket === 'p12') {
          reasons.push('Ø24 cm is de maat voor 1 tot 2 personen.');
        } else if (answers.personen === 'p24' && p.size_bucket === 'p24') {
          reasons.push('Ø28 cm is de gezinsmaat: 2 tot 4 personen.');
        } else if (answers.personen === 'p34' && p.size_bucket === 'p24') {
          reasons.push('Ø28 cm geeft ruimte voor grote porties: zo\'n 3 kipfilets naast elkaar.');
        } else if (answers.personen === 'p34' && (p.size_bucket === 'p34' || p.size_bucket === 'set')) {
          reasons.push('Gemaakt voor grote porties: 3 tot 4+ personen.');
        } else if (answers.personen === 'wisselend' && p.size_bucket === 'set') {
          reasons.push('Twee maten in één set: altijd de passende pan bij de hand.');
        }

        return reasons.slice(0, 3);
      },
      // Gelijkspel: eerst de gezinsmaat Ø28 (p24), daarna hybride boven RVS
      // (laagdrempeliger instap). rankProducts checkt bestseller al vóór de tiebreak.
      tiebreak: function (a, b) {
        var pref = { p24: 3, set: 2, p34: 1, p12: 0 };
        var d = (pref[b.size_bucket] || 0) - (pref[a.size_bucket] || 0);
        if (d !== 0) return d;
        if (a.pantype !== b.pantype) return a.pantype === 'hybride' ? -1 : 1;
        return 0;
      }
    }
  };
  function getSid() {
    try {
      var k = 'sophior_keuzehulp_sid';
      var v = window.localStorage.getItem(k);
      if (!v) { v = 'kh_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8); window.localStorage.setItem(k, v); }
      return v;
    } catch (e) { return ''; }
  }
  function pushAnalytics(event, data) {
    var payload = Object.assign({ event: event, keuzehulp_sid: getSid() }, data || {});
    if (window.dataLayer && typeof window.dataLayer.push === 'function') {
      window.dataLayer.push(payload);
    }
    // Brug naar Shopify Customer Events: gesandboxte custom pixels kunnen niet
    // bij window.dataLayer, wel bij events die via Shopify.analytics.publish
    // gepubliceerd worden (analytics.subscribe('keuzehulp_...') in de pixel).
    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      try { window.Shopify.analytics.publish(event, payload); } catch (e) { /* no-op */ }
    }
    document.dispatchEvent(new CustomEvent('sophior:analytics', { detail: payload }));
  }

  /* ---------- scoring ---------- */
  // Gewogen som over de facetten van het actieve profiel; per-facet scoring
  // komt uit profile.facetMatch (0..1).
  function scoreProduct(p, answers, weights, profile) {
    var sum = 0, totalW = 0;
    profile.facets.forEach(function (facet) {
      var value = answers[facet];
      if (!value) return;
      var w = weights[facet] || 0;
      if (w <= 0) return;
      totalW += w;
      sum += w * profile.facetMatch(p, facet, value);
    });
    var pct = totalW > 0 ? Math.round((sum / totalW) * 100) : 0;
    return pct;
  }

  // Rangschik producten, dedupliceer op maatgroep zodat de top-3 verschillende
  // productlijnen zijn (niet drie maten van dezelfde plank).
  function rankProducts(products, answers, weights, profile) {
    // Uitverkochte producten niet aanbevelen; alleen als álles uitverkocht is
    // vallen we terug op de volledige lijst (ATC wordt dan verborgen).
    var pool = products.filter(function (p) { return p.available !== false; });
    if (!pool.length) pool = products;
    var isCadeau = answers[profile.giftFacet] === 'cadeau';
    var scored = pool.map(function (p) {
      // Cadeau: bestsellers eerst, maar de overige antwoorden (uitstraling,
      // ruimte, ...) bepalen de volgorde bínnen die groep.
      return { p: p, score: isCadeau ? (p.bestseller ? 1000 : 0) + scoreProduct(p, answers, weights, profile) : scoreProduct(p, answers, weights, profile) };
    });
    scored.sort(function (a, b) {
      if (b.score !== a.score) return b.score - a.score;
      if (a.p.bestseller !== b.p.bestseller) return a.p.bestseller ? -1 : 1;
      var tb = profile.tiebreak(a.p, b.p);
      if (tb !== 0) return tb;
      return a.p.price - b.p.price;
    });
    var seen = {}, out = [];
    for (var i = 0; i < scored.length && out.length < 3; i++) {
      var key = scored[i].p.group;
      if (seen[key]) continue;
      seen[key] = true;
      out.push(scored[i]);
    }
    return out;
  }

  /* ---------- init per sectie ---------- */
  function initSection(root) {
    // Keuzehulp v2 (redesign golf 3, s04): eigen gedrag in assets/sophior-keuzehulp-v2.js,
    // met dezelfde scoring hieronder (window.SophiorKeuzehulp). Dit script laat die sectie met rust.
    if (root.hasAttribute('data-keuzehulp-v2')) return;
    if (root.dataset.keuzehulpInit === '1') return;
    root.dataset.keuzehulpInit = '1';

    // Actief profiel bepalen (snijplank/pan); onbekend type valt terug op snijplank.
    var profile = PROFILES[root.dataset.keuzehulpType] || PROFILES.snijplank;
    // Analytics-wrapper: verrijkt elk event met het keuzehulp-type. Eventnamen ongewijzigd.
    function track(event, data) {
      pushAnalytics(event, Object.assign({ keuzehulp_type: root.dataset.keuzehulpType || 'snijplank' }, data || {}));
    }

    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var indexEl = root.querySelector('[data-keuzehulp-index]');
    var products = [];
    try { products = indexEl ? JSON.parse(indexEl.textContent) : []; } catch (e) { products = []; }

    var quiz = root.querySelector('[data-keuzehulp-quiz]');
    if (!quiz || !products.length) return; // niets te verrijken

    var steps = Array.prototype.slice.call(root.querySelectorAll('[data-keuzehulp-step]'));
    var resultBox = root.querySelector('[data-keuzehulp-result]');
    var live = root.querySelector('[data-keuzehulp-live]');
    var progress = root.querySelector('[data-keuzehulp-progress]');
    var progressText = root.querySelector('[data-keuzehulp-progress-text]');
    var ringsSvg = root.querySelector('[data-keuzehulp-rings]');
    var SVG_NS = 'http://www.w3.org/2000/svg';
    var nextBtn = root.querySelector('[data-keuzehulp-next]');
    var backBtn = root.querySelector('[data-keuzehulp-back]');
    var submitBtn = root.querySelector('[data-keuzehulp-submit]');

    var nextLabel = nextBtn ? (nextBtn.textContent.trim() || 'Volgende') : 'Volgende';
    var submitLabel = submitBtn ? (submitBtn.textContent.trim() || 'Toon mijn snijplank') : 'Toon mijn snijplank';
    var restartLabel = root.dataset.restartLabel || 'Opnieuw beginnen';

    var weights = {};
    steps.forEach(function (s) { weights[s.dataset.facet] = parseInt(s.dataset.weight, 10) || 0; });

    var progressTpl = root.dataset.progressTemplate || 'Stap [current] van [total]';
    var matchTpl = root.dataset.matchTemplate || '[score]% match';
    var labels = {
      result: root.dataset.resultHeading || 'Onze aanbeveling voor jou',
      alt: root.dataset.altHeading || 'Ook goed geschikt',
      cta: root.dataset.ctaLabel || 'Bekijk deze snijplank',
      size: root.dataset.sizeLabel || 'Kies je maat',
      reasons: root.dataset.reasonsLabel || 'Waarom dit bij je past',
      empty: root.dataset.emptyLabel || 'We konden geen match bepalen.',
      atc: root.dataset.atcLabel || 'In winkelwagen',
      adding: root.dataset.addingLabel || 'Toevoegen...',
      added: root.dataset.addedLabel || 'Toegevoegd',
      cartError: root.dataset.cartError || 'Toevoegen mislukt, probeer het opnieuw.',
      view: root.dataset.viewLabel || 'Bekijk productpagina',
      addonLabel: root.dataset.addonLabel || 'Maak het compleet:',
      addonHint: root.dataset.addonHint || 'in combi',
      primaryBadge: root.dataset.primaryBadge || 'Beste match',
      adjust: root.dataset.adjustLabel || 'Pas je antwoorden aan',
      soldout: root.dataset.soldoutLabel || 'Tijdelijk uitverkocht',
      summary: root.dataset.summaryLabel || 'Op basis van jouw antwoorden',
      answerRequired: root.dataset.answerRequired || 'Kies eerst een antwoord om verder te gaan'
    };

    var errorEl = root.querySelector('[data-keuzehulp-error]');
    function showAnswerError() {
      if (!errorEl) return;
      errorEl.hidden = false;
      errorEl.textContent = labels.answerRequired;
    }
    function clearAnswerError() {
      if (!errorEl || errorEl.hidden) return;
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    var state = { current: 0, answers: {} };
    var started = false; // keuzehulp_start vuurt bij de eerste interactie, niet bij pageview

    root.classList.add('sophior-keuzehulp--enhanced');
    if (progress) progress.hidden = false;
    if (backBtn) backBtn.hidden = false;

    // Jaarringen-voortgang: één concentrische ring per vraag, van binnen naar buiten
    if (ringsSvg && steps.length) {
      var maxSpread = steps.length > 1 ? steps.length - 1 : 1;
      for (var ri = 0; ri < steps.length; ri++) {
        var ringCircle = document.createElementNS(SVG_NS, 'circle');
        ringCircle.setAttribute('cx', '28');
        ringCircle.setAttribute('cy', '28');
        ringCircle.setAttribute('r', String(7 + Math.round(ri * (18 / maxSpread))));
        ringCircle.setAttribute('stroke-width', (1.7 - ri * 0.18).toFixed(2));
        ringsSvg.appendChild(ringCircle);
      }
    }
    function updateRings() {
      if (!ringsSvg) return;
      var answered = Object.keys(state.answers).length;
      Array.prototype.forEach.call(ringsSvg.children, function (c, i) {
        c.classList.toggle('sophior-keuzehulp__ring--grown', i < answered);
      });
    }
    // De Volgende/Toon-knop blijft zichtbaar: auto-advance geldt alleen voor
    // muis/touch; toetsenbord- en screenreadergebruikers navigeren met de knop
    // (WCAG 3.2.2: geen contextwissel door alleen een radiokeuze).

    /* ----- rendering van stappen ----- */
    function renderStep() {
      steps.forEach(function (s, i) {
        s.classList.toggle('sophior-keuzehulp__step--active', i === state.current);
      });
      var total = steps.length;
      var human = state.current + 1;
      clearAnswerError();
      if (progressText) progressText.textContent = progressTpl.replace('[current]', human).replace('[total]', total);
      updateRings();
      if (backBtn) backBtn.hidden = state.current === 0;
      if (nextBtn) nextBtn.hidden = state.current >= total - 1;
      if (submitBtn) submitBtn.hidden = state.current < total - 1;
      if (live) live.textContent = progressTpl.replace('[current]', human).replace('[total]', total);

      // focus naar de legend van de actieve stap (niet bij de allereerste render/laden)
      var activeStep = steps[state.current];
      if (activeStep && root.dataset.keuzehulpBooted === '1') {
        var legend = activeStep.querySelector('legend');
        if (legend) { legend.setAttribute('tabindex', '-1'); legend.focus(); }
      }
    }

    function hasAnswerForStep(i) {
      var facet = steps[i] && steps[i].dataset.facet;
      return facet ? !!state.answers[facet] : false;
    }

    /* ----- antwoorden ----- */
    var advanceTimer = null;
    var lastPointer = 0; // onderscheid muis/touch van toetsenbord voor auto-advance
    quiz.addEventListener('pointerdown', function () { lastPointer = Date.now(); }, true);
    quiz.addEventListener('change', function (e) {
      var input = e.target;
      if (!input || input.type !== 'radio') return;
      var facet = input.getAttribute('data-facet');
      var value = input.getAttribute('data-value');
      if (!facet) return;
      state.answers[facet] = value;
      clearAnswerError();
      writeURL(true);
      if (!started) {
        started = true;
        track('keuzehulp_start', {});
      }
      track('keuzehulp_question_answered', { step: state.current + 1, facet: facet, answer: value });
      // Laatste vraag beantwoord: de laatste ring kleurt vóór het advies verschijnt.
      updateRings();
      // Auto-advance alleen bij muis/touch: wie met pijltjestoetsen de opties
      // verkent (elke pijl = change) mag niet weggenavigeerd worden (WCAG 3.2.2);
      // die groep gebruikt de zichtbare Volgende/Toon-knop.
      if (advanceTimer) clearTimeout(advanceTimer);
      var byPointer = Date.now() - lastPointer < 500;
      if (!byPointer) return;
      advanceTimer = setTimeout(function () {
        if (state.current < steps.length - 1) { state.current += 1; renderStep(); scrollTop(); }
        else { showResult(); }
      }, 420);
    });

    if (nextBtn) nextBtn.addEventListener('click', function () {
      if (!hasAnswerForStep(state.current)) { showAnswerError(); return; }
      if (state.current < steps.length - 1) {
        state.current += 1;
        renderStep();
        scrollTop();
      } else {
        showResult();
      }
    });
    if (backBtn) backBtn.addEventListener('click', function () {
      if (state.current > 0) { state.current -= 1; renderStep(); scrollTop(); }
    });
    var restartBtn = root.querySelector('[data-keuzehulp-restart]');
    if (restartBtn) restartBtn.addEventListener('click', function () { resetQuiz(); });

    // Enter / no-JS submit netjes afvangen: alleen resultaat als alles beantwoord is
    quiz.addEventListener('submit', function (e) {
      e.preventDefault();
      if (allAnswered()) showResult();
      else if (nextBtn) nextBtn.click();
    });
    function allAnswered() {
      return steps.every(function (s) { return !!state.answers[s.dataset.facet]; });
    }

    function scrollTop() {
      if (reduceMotion) return;
      var top = quiz.getBoundingClientRect().top + window.pageYOffset - 90;
      window.scrollTo({ top: top, behavior: 'smooth' });
    }

    /* ----- resultaat ----- */
    function showResult(fromHistory) {
      var ranked = rankProducts(products, state.answers, weights, profile);
      resultBox.innerHTML = '';
      resultBox.hidden = false;

      if (!ranked.length) {
        resultBox.appendChild(el('p', 'sophior-keuzehulp__empty', labels.empty));
        return;
      }
      var isCadeau = state.answers[profile.giftFacet] === 'cadeau';

      // primaire match
      var primary = ranked[0];
      resultBox.appendChild(el('h2', 'sophior-keuzehulp__result-heading', labels.result));

      // persoonlijke samenvatting: de uitslag hoort bij déze antwoorden
      var answered = [];
      steps.forEach(function (s) {
        var checked = s.querySelector('input:checked');
        if (!checked) return;
        var txt = checked.closest('label');
        txt = txt && txt.querySelector('.sophior-keuzehulp__answer-text');
        if (txt) answered.push(txt.textContent.trim());
      });
      if (answered.length) {
        var summary = el('p', 'sophior-keuzehulp__result-summary');
        summary.appendChild(el('strong', 'sophior-keuzehulp__result-summary-label', labels.summary + ': '));
        summary.appendChild(document.createTextNode(answered.join(' · ')));
        resultBox.appendChild(summary);
      }

      // het advies zelf ("waarom dit bij je past") hoort bij de uitslag,
      // vóór de productkaart, niet in het koopgedeelte
      var adviceReasons = profile.buildReasons(primary.p, state.answers);
      if (adviceReasons.length) {
        var advice = el('div', 'sophior-keuzehulp__reasons sophior-keuzehulp__reasons--advice');
        advice.appendChild(el('p', 'sophior-keuzehulp__reasons-label', labels.reasons));
        var adviceList = el('ul', 'sophior-keuzehulp__reasons-list');
        adviceReasons.forEach(function (txt) { adviceList.appendChild(el('li', 'sophior-keuzehulp__reason', txt)); });
        advice.appendChild(adviceList);
        resultBox.appendChild(advice);
      }

      resultBox.appendChild(buildPrimaryCard(primary, isCadeau));

      // alternatieven
      if (ranked.length > 1) {
        resultBox.appendChild(el('h3', 'sophior-keuzehulp__alt-heading', labels.alt));
        var altWrap = el('div', 'sophior-keuzehulp__alts');
        ranked.slice(1).forEach(function (r) { altWrap.appendChild(buildAltCard(r, isCadeau)); });
        resultBox.appendChild(altWrap);
      }

      // e-mailcapture bewust verwijderd (besluit JP 7 aug 2026): converteerde
      // 0,3% en stond het koop-pad in de weg; nieuwsbrief loopt via de footer.

      // antwoorden aanpassen of opnieuw beginnen, zichtbaar vanuit het resultaat
      var actions = el('div', 'sophior-keuzehulp__result-actions');
      var adjustBtn = el('button', 'sophior-keuzehulp__btn sophior-keuzehulp__btn--ghost', labels.adjust);
      adjustBtn.type = 'button';
      adjustBtn.addEventListener('click', function () { showQuiz(); });
      actions.appendChild(adjustBtn);
      var againBtn = el('button', 'sophior-keuzehulp__btn sophior-keuzehulp__btn--ghost', restartLabel);
      againBtn.type = 'button';
      againBtn.addEventListener('click', function () { resetQuiz(); });
      actions.appendChild(againBtn);
      resultBox.appendChild(actions);

      // alleen de vragenkaart en de productlijst verbergen: de intro links blijft
      // staan zodat het resultaat op dezelfde plek als uitslag verschijnt
      quiz.hidden = true;
      var allGrid = root.querySelector('[data-keuzehulp-all]');
      if (allGrid) allGrid.hidden = true;
      if (live) live.textContent = labels.result + ': ' + primary.p.title;
      resultBox.setAttribute('tabindex', '-1');
      resultBox.focus();
      if (!fromHistory) writeURL(false, true);
      track('keuzehulp_result_shown', {
        primary_id: primary.p.id, primary_score: primary.score,
        alt_ids: ranked.slice(1).map(function (r) { return r.p.id; })
      });
    }

    // Terug naar de vragen met behoud van de gegeven antwoorden.
    function showQuiz(fromHistory) {
      resultBox.hidden = true;
      resultBox.innerHTML = '';
      quiz.hidden = false;
      var allGrid = root.querySelector('[data-keuzehulp-all]');
      if (allGrid) allGrid.hidden = false;
      state.current = 0;
      renderStep();
      if (!fromHistory) writeURL(true);
      scrollTop();
    }

    // Primaire kaart: kwalitatieve badge ("Beste match") — een kaal percentage
    // ondermijnt de aanbeveling. Alternatieven tonen wel hun match-percentage.
    // "Bestseller" alleen als het product dat echt is; in de cadeau-route zegt
    // het percentage niets, dus alternatieven krijgen daar geen badge.
    function badgeText(r, isCadeau, isPrimary) {
      if (isCadeau) {
        if (r.p.bestseller) return 'Bestseller';
        return isPrimary ? labels.primaryBadge : '';
      }
      if (isPrimary) return labels.primaryBadge;
      return matchTpl.replace('[score]', r.score);
    }

    function buildPrimaryCard(r, isCadeau) {
      var p = r.p;
      var card = el('article', 'sophior-keuzehulp__card sophior-keuzehulp__card--primary');

      var media = el('a', 'sophior-keuzehulp__card-media');
      media.href = p.url;
      if (p.image) {
        var img = el('img', 'sophior-keuzehulp__card-img');
        img.src = p.image; img.alt = p.image_alt || p.title;
        img.loading = 'lazy'; img.width = 600; img.height = 600;
        media.appendChild(img);
      }
      var badgeTxt = badgeText(r, isCadeau, true);
      if (badgeTxt) media.appendChild(el('span', 'sophior-keuzehulp__badge', badgeTxt));
      card.appendChild(media);

      var body = el('div', 'sophior-keuzehulp__card-body');
      body.appendChild(el('p', 'sophior-keuzehulp__card-brand', 'SOPHIOR'));
      body.appendChild(el('h3', 'sophior-keuzehulp__card-title', p.title));
      if (p.core_promise) body.appendChild(el('p', 'sophior-keuzehulp__card-promise', p.core_promise));
      body.appendChild(el('p', 'sophior-keuzehulp__card-price', p.price_money));

      // (de onderbouwing staat als advies-blok bovenin de uitslag, zie showResult)

      // maatkiezer
      if (p.siblings && p.siblings.length > 1) {
        var sizeWrap = el('div', 'sophior-keuzehulp__sizes');
        sizeWrap.appendChild(el('p', 'sophior-keuzehulp__sizes-label', labels.size));
        var pills = el('div', 'sophior-keuzehulp__pills');
        p.siblings.forEach(function (s) {
          var pill;
          if (s.current) {
            pill = el('span', 'sophior-keuzehulp__pill sophior-keuzehulp__pill--active', s.label);
            pill.setAttribute('aria-current', 'true');
          } else {
            pill = el('a', 'sophior-keuzehulp__pill' + (s.available ? '' : ' sophior-keuzehulp__pill--out'), s.label);
            pill.href = s.url;
          }
          pills.appendChild(pill);
        });
        sizeWrap.appendChild(pills);
        body.appendChild(sizeWrap);
      }

      // bundel-upsell (add-on olie via combideal)
      var addonChecked = { value: false };
      if (p.addon && p.addon.id) {
        var addonWrap = el('label', 'sophior-keuzehulp__addon');
        var cb = document.createElement('input');
        cb.type = 'checkbox';
        cb.className = 'sophior-keuzehulp__addon-check';
        addonWrap.appendChild(cb);
        var inner = el('span', 'sophior-keuzehulp__addon-inner');
        if (p.addon.image) {
          var aimg = el('img', 'sophior-keuzehulp__addon-img');
          aimg.src = p.addon.image; aimg.alt = p.addon.title; aimg.loading = 'lazy'; aimg.width = 80; aimg.height = 80;
          inner.appendChild(aimg);
        }
        var adet = el('span', 'sophior-keuzehulp__addon-det');
        adet.appendChild(el('span', 'sophior-keuzehulp__addon-name', labels.addonLabel + ' ' + p.addon.title));
        var aprice = el('span', 'sophior-keuzehulp__addon-price');
        aprice.appendChild(el('span', 'sophior-keuzehulp__addon-compare', p.addon.price_money));
        aprice.appendChild(el('span', 'sophior-keuzehulp__addon-new', p.addon.new_money + ' ' + labels.addonHint));
        adet.appendChild(aprice);
        inner.appendChild(adet);
        addonWrap.appendChild(inner);
        cb.addEventListener('change', function () { addonChecked.value = cb.checked; updateAtc(); });
        body.appendChild(addonWrap);
      }

      // In winkelwagen (voegt de plank toe, plus de olie als aangevinkt)
      var atc = el('button', 'sophior-keuzehulp__btn sophior-keuzehulp__btn--primary sophior-keuzehulp__cta');
      atc.type = 'button';
      function updateAtc() {
        if (addonChecked.value && p.addon) atc.textContent = labels.atc + ' — ' + p.addon.bundle_total_money;
        else atc.textContent = labels.atc + (p.price_money ? ' — ' + p.price_money : '');
      }
      updateAtc();
      if (p.variant_id && p.available !== false) {
        atc.addEventListener('click', function () { addToCart(p, addonChecked.value, atc); });
        body.appendChild(atc);
      } else if (p.available === false) {
        body.appendChild(el('p', 'sophior-keuzehulp__soldout', labels.soldout));
      }

      // vertrouwen: reviewscore + zekerheden (kloon van het server-rendered blok)
      var trustTpl = root.querySelector('[data-keuzehulp-trust]');
      if (trustTpl) {
        var trust = trustTpl.cloneNode(true);
        trust.hidden = false;
        trust.removeAttribute('data-keuzehulp-trust');
        body.appendChild(trust);
      }

      // secundaire link naar de productpagina
      var view = el('a', 'sophior-keuzehulp__view-link', labels.view);
      view.href = p.url;
      view.addEventListener('click', function () { track('keuzehulp_result_click', { product_id: p.id, position: 1 }); });
      body.appendChild(view);

      card.appendChild(body);
      return card;
    }

    function addToCart(p, withAddon, btn) {
      var items = [{ id: p.variant_id, quantity: 1 }];
      if (withAddon && p.addon && p.addon.id) items.push({ id: p.addon.id, quantity: 1 });
      var original = btn.textContent;
      btn.disabled = true;
      btn.textContent = labels.adding;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: items })
      }).then(function (r) {
        if (!r.ok) throw new Error('cart');
        return r.json();
      }).then(function () {
        btn.textContent = labels.added + ' ✓';
        document.dispatchEvent(new CustomEvent('sophior:cart-updated'));
        /* Uniforme flow: cart drawer openen (of terugvallen op /cart) net als de PDP */
        document.dispatchEvent(new CustomEvent('sophior:open-cart'));
        track('keuzehulp_add_to_cart', { product_id: p.id, bundle: !!withAddon });
        if (live) live.textContent = labels.added;
        setTimeout(function () { btn.disabled = false; btn.textContent = original; }, 1600);
      }).catch(function () {
        btn.disabled = false;
        btn.textContent = labels.cartError;
        if (live) live.textContent = labels.cartError;
      });
    }

    function buildAltCard(r, isCadeau) {
      var p = r.p;
      var card = el('article', 'sophior-keuzehulp__card sophior-keuzehulp__card--alt');
      var media = el('a', 'sophior-keuzehulp__card-media');
      media.href = p.url;
      if (p.image) {
        var img = el('img', 'sophior-keuzehulp__card-img');
        img.src = p.image; img.alt = p.image_alt || p.title;
        img.loading = 'lazy'; img.width = 600; img.height = 600;
        media.appendChild(img);
      }
      var altBadge = badgeText(r, isCadeau, false);
      if (altBadge) media.appendChild(el('span', 'sophior-keuzehulp__badge sophior-keuzehulp__badge--sm', altBadge));
      card.appendChild(media);

      var body = el('div', 'sophior-keuzehulp__card-body');
      body.appendChild(el('h3', 'sophior-keuzehulp__card-title sophior-keuzehulp__card-title--sm', p.title));
      body.appendChild(el('p', 'sophior-keuzehulp__card-price', p.price_money));
      var cta = el('a', 'sophior-keuzehulp__alt-link', labels.cta);
      cta.href = p.url;
      cta.addEventListener('click', function () {
        track('keuzehulp_result_click', { product_id: p.id, position: 2 });
      });
      body.appendChild(cta);
      card.appendChild(body);
      return card;
    }

    function resetQuiz() {
      state = { current: 0, answers: {} };
      quiz.querySelectorAll('input[type="radio"]').forEach(function (i) { i.checked = false; });
      resultBox.hidden = true;
      resultBox.innerHTML = '';
      quiz.hidden = false;
      var allGrid = root.querySelector('[data-keuzehulp-all]');
      if (allGrid) allGrid.hidden = false;
      renderStep();
      writeURL(true);
      scrollTop();
      track('keuzehulp_restart', {});
    }

    /* ----- URL-sync (deelbare resultaat-URL) ----- */
    function writeURL(replace, isResult) {
      if (!window.history || !window.history.replaceState) return;
      var params = new URLSearchParams();
      profile.facets.forEach(function (f) { if (state.answers[f]) params.set(f, state.answers[f]); });
      if (isResult) params.set('resultaat', '1');
      var qs = params.toString();
      var url = window.location.pathname + (qs ? '?' + qs : '');
      try {
        if (replace) window.history.replaceState({ kh: 1 }, '', url);
        else window.history.pushState({ kh: 1 }, '', url);
      } catch (e) { /* no-op */ }
    }
    function readURL() {
      var params = new URLSearchParams(window.location.search);
      var any = false;
      profile.facets.forEach(function (f) {
        var v = params.get(f);
        if (v) {
          state.answers[f] = v;
          any = true;
          var input = quiz.querySelector('input[data-facet="' + f + '"][data-value="' + cssEscape(v) + '"]');
          if (input) input.checked = true;
        }
      });
      return { any: any, result: params.get('resultaat') === '1' };
    }
    function cssEscape(s) { return String(s).replace(/"/g, '\\"'); }

    /* ----- browser-terugknop: URL-state terugvertalen naar de juiste weergave ----- */
    window.addEventListener('popstate', function () {
      var params = new URLSearchParams(window.location.search);
      var wantsResult = params.get('resultaat') === '1';
      if (wantsResult && resultBox.hidden) {
        readURL();
        if (allAnswered()) showResult(true);
      } else if (!wantsResult && !resultBox.hidden) {
        showQuiz(true);
      }
    });

    /* ----- boot ----- */
    var fromURL = readURL();
    renderStep();
    root.dataset.keuzehulpBooted = '1';
    if (fromURL.any) started = true; // herstelde/gedeelde sessie telt niet opnieuw als start
    if (fromURL.any && fromURL.result) {
      showResult();
    } else if (fromURL.any) {
      // ga naar de eerste onbeantwoorde stap
      var firstUnanswered = 0;
      for (var i = 0; i < steps.length; i++) {
        if (!state.answers[steps[i].dataset.facet]) { firstUnanswered = i; break; }
        firstUnanswered = Math.min(i + 1, steps.length - 1);
      }
      state.current = firstUnanswered;
      renderStep();
    }
  }

  // De scoring (profielen, score en rangschikking) is gedeeld met keuzehulp v2 (redesign golf 3,
  // s04-beslishulp): één engine; v2 past alleen de facet "prijs" aan (live prijs, D08 §3.5 punt 11).
  window.SophiorKeuzehulp = window.SophiorKeuzehulp || { profielen: PROFILES, score: scoreProduct, rangschik: rankProducts };

  function initAll() {
    document.querySelectorAll('[data-keuzehulp]').forEach(initSection);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
